// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * Plan 1, "straight to the first win" (night goal 2026-09-25, build list item 1).
 *
 * The rules behind the first-run path, kept out of the screens so they can be
 * checked without a window: which sample sets to offer and how a sample job is
 * named, the one-card-per-stage setup wait, why a document is waiting in plain
 * words, the three-step first-review strip, and the on-device milestone times.
 *
 * Checked by tests/onboarding-tests/run.mjs (`npm run test:onboarding`).
 */

import type { OnboardingMilestone } from '../shared/engagement';
import type { FieldResult } from '../shared/desktop-api';
import { hostHas } from '../shared/host-capabilities';
import { fieldProblemWords, type ProblemDocument } from '../paige/field-problem-words';

// ---------------------------------------------------------------------------
// Sample sets
// ---------------------------------------------------------------------------

export type SampleStack = { corpus: string; label: string; documentCount: number; ready?: boolean };
export type SampleSetChoice = SampleStack & { description: string };

/** What each shipped industry set holds, in the words its paperwork uses. */
const SET_DESCRIPTIONS: Record<string, string> = {
  accounts_payable: 'Invoices, purchase orders and credit memos',
  auto_dealership: 'Buyer orders, contracts and title applications',
  freight_logistics: 'Bills of lading, packing slips and freight invoices',
  healthcare_rcm: 'Claims, superbills and patient statements',
  hr: 'Offer letters, tax forms and performance reviews',
  insurance: 'Claim forms, applications and policies',
  legal: 'Engagement letters, contracts and legal invoices',
  mortgage: 'Pay stubs, bank statements and closing disclosures',
  pc_claims: 'Adjuster reports, police reports and witness statements',
  student_records: 'Transcripts, admission applications and health records',
  utility_billing: 'Electric bills, gas bills and statements',
};

/**
 * Words that say which kind of paperwork a job is about, per set. A job called
 * "AP invoices" should get the Accounts Payable tray, not whatever sorts first.
 */
const SET_KEYWORDS: Record<string, readonly string[]> = {
  accounts_payable: [
    'accounts payable',
    'ap',
    'payable',
    'payables',
    'invoice',
    'invoices',
    'vendor',
    'purchase order',
    'credit memo',
  ],
  auto_dealership: ['auto', 'dealership', 'dealer', 'vehicle', 'car', 'deal jacket'],
  freight_logistics: [
    'freight',
    'logistics',
    'shipping',
    'lading',
    'bol',
    'packing slip',
    'carrier',
  ],
  healthcare_rcm: ['healthcare', 'medical', 'patient', 'rcm', 'superbill', 'clinic', 'hospital'],
  hr: [
    'hr',
    'human resources',
    'employee',
    'employees',
    'onboarding',
    'personnel',
    'payroll',
    'offer letter',
  ],
  insurance: ['insurance', 'policy', 'policies', 'underwriting'],
  legal: ['legal', 'law', 'attorney', 'court', 'contract', 'contracts', 'engagement'],
  mortgage: ['mortgage', 'loan', 'loans', 'lending', 'closing'],
  pc_claims: ['claims', 'claim', 'p&c', 'adjuster', 'property', 'casualty', 'loss'],
  student_records: [
    'student',
    'students',
    'school',
    'transcript',
    'transcripts',
    'admissions',
    'enrollment',
    'college',
  ],
  utility_billing: ['utility', 'utilities', 'electric', 'gas bill', 'water bill', 'meter'],
};

const setKey = (corpus: string) => corpus.replace(/_(small|large)_set$/, '');

/**
 * The welcome's sample choices: the shipped industry sets, A to Z by name.
 * When any set ships a pre-trained job, only those are offered: trying one
 * opens straight to review, while a set without one would start an hour-long
 * setup, which is the opposite of a quick look. A development stack (anything
 * that is not a `_small_set`) is left out whenever the industry sets are installed.
 */
export function sampleSetChoices(stacks: readonly SampleStack[]): SampleSetChoice[] {
  const industry = stacks.filter((stack) => stack.corpus.endsWith('_small_set'));
  const installed =
    industry.length > 0 ? industry : stacks.filter((stack) => !stack.corpus.endsWith('_large_set'));
  const ready = installed.filter((stack) => stack.ready === true);
  const shown = ready.length > 0 ? ready : installed;
  return [...shown]
    .sort((left, right) => left.label.localeCompare(right.label, 'en'))
    .map((stack) => ({
      ...stack,
      description: SET_DESCRIPTIONS[setKey(stack.corpus)] ?? 'Sample documents',
    }));
}

/** One or two words per set, for the picture cards. */
const SET_SHORT_NAMES: Record<string, string> = {
  accounts_payable: 'Invoices',
  auto_dealership: 'Car sales',
  freight_logistics: 'Freight',
  healthcare_rcm: 'Healthcare',
  hr: 'HR',
  insurance: 'Insurance',
  legal: 'Legal',
  mortgage: 'Mortgage',
  pc_claims: 'Claims',
  student_records: 'Students',
  utility_billing: 'Utilities',
};

export const sampleShortName = (choice: { corpus: string; label: string }) =>
  SET_SHORT_NAMES[setKey(choice.corpus)] ?? choice.label;

/**
 * Which set is dropped first to keep the rows full. Ordered by the week-one
 * test (2026-09-25): the sets whose taught job read unseen documents worst go
 * first. Sets not listed go before listed ones.
 */
const SET_STRENGTH = [
  'accounts_payable',
  'freight_logistics',
  'mortgage',
  'auto_dealership',
  'hr',
  'insurance',
  'legal',
  'healthcare_rcm',
];

/**
 * The cards in full rows, never a lone last row (owner review, 2026-09-27): an
 * odd count drops its weakest set, and the columns split what is left into at
 * most two even rows (8 → 4×2, 10 → 5×2, 6 → 3×2; up to 5 fit one row).
 * Invoices lead; the rest keep their A to Z order.
 */
export function sampleCardGrid<T extends { corpus: string }>(
  choices: readonly T[],
): { cards: T[]; columns: number } {
  const rank = (choice: T) => {
    const index = SET_STRENGTH.indexOf(setKey(choice.corpus));
    return index < 0 ? SET_STRENGTH.length : index;
  };
  let cards = [...choices];
  if (cards.length > 5 && cards.length % 2 === 1) {
    const weakest = cards.reduce(
      (worst, choice) => (rank(choice) > rank(worst) ? choice : worst),
      cards[0],
    );
    cards = cards.filter((choice) => choice !== weakest);
  }
  const flagship = cards.findIndex((choice) => setKey(choice.corpus) === 'accounts_payable');
  if (flagship > 0) cards = [cards[flagship], ...cards.filter((_, index) => index !== flagship)];
  return {
    cards,
    columns: cards.length <= 5 ? Math.max(1, cards.length) : Math.ceil(cards.length / 2),
  };
}

// ---------------------------------------------------------------------------
// Sample jobs (owner decision 1: only when asked, and labelled as a sample)
// ---------------------------------------------------------------------------

export const SAMPLE_JOB_PREFIX = 'Sample · ';
export const SAMPLE_JOB_DESCRIPTION = 'Sample job · Ademero sample documents';

/** "Sample · Accounts Payable", numbered when that name is taken. */
export function sampleJobName(label: string, existingNames: readonly string[]): string {
  const taken = new Set(existingNames.map((name) => name.trim().toLocaleLowerCase()));
  const base = `${SAMPLE_JOB_PREFIX}${label}`;
  if (!taken.has(base.toLocaleLowerCase())) return base;
  for (let number = 2; ; number += 1) {
    const candidate = `${base} ${number}`;
    if (!taken.has(candidate.toLocaleLowerCase())) return candidate;
  }
}

/**
 * The status-bar line when a sample job opens. When its reader weights have not arrived (the operator
 * chose "Open now", or the download failed or was offline), it stays short enough to sit beside the
 * download indicator without being cut off, and promises nothing about a download in flight, which
 * an offline computer does not have.
 */
export function sampleJobReadyNotice(
  name: string,
  { pretrained, readerPending }: { pretrained: boolean; readerPending: boolean },
): string {
  if (!pretrained) return `${name} created`;
  return readerPending
    ? `${name} is ready · its sample reader switches on once it downloads`
    : `${name} is ready · learned from Ademero sample documents`;
}

export function isSampleJob(job: { name: string; description?: string | null }): boolean {
  return job.name.startsWith(SAMPLE_JOB_PREFIX) || job.description === SAMPLE_JOB_DESCRIPTION;
}

/** The shape CreateJobModal saves for a "start scanning" job, labelled as a sample. */
export type SampleJobDraft = {
  id: string;
  name: string;
  description: string;
  accent: string;
  bootstrapMode: 'scan';
  status: 'provisional';
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  documentTypes: never[];
  separationMode: 'automatic';
  separationBarcodeText: null;
  boundaryOpeningProfiles: never[];
  boundaryContinuationProfiles: never[];
  suppressedSuggestions: never[];
  suggestionsPausedUntil: null;
};

export function buildSampleJob({
  id,
  label,
  existingNames,
  now,
}: {
  id: string;
  label: string;
  corpus: string;
  existingNames: readonly string[];
  now: string;
}): SampleJobDraft {
  return {
    id,
    name: sampleJobName(label, existingNames),
    description: SAMPLE_JOB_DESCRIPTION,
    accent: '#087f9c',
    bootstrapMode: 'scan',
    status: 'provisional',
    schemaVersion: 0,
    createdAt: now,
    updatedAt: now,
    documentTypes: [],
    separationMode: 'automatic',
    separationBarcodeText: null,
    boundaryOpeningProfiles: [],
    boundaryContinuationProfiles: [],
    suppressedSuggestions: [],
    suggestionsPausedUntil: null,
  };
}

const SAFE_SET = /^[a-z0-9][a-z0-9_]{0,79}$/i;

/**
 * The seam for "Try with sample documents".
 *
 * First asks for the set's pre-trained job setup (build item 6 ships one per set
 * as a job setup file, imported through the ordinary job-template import). When
 * the set has none, which is every set today, it creates an ordinary new job
 * labelled as a sample. Either way the caller then opens the job and starts the
 * simulated scanner on `corpus`.
 */
export async function startSampleJob<J extends { id: string; name: string }>(
  setId: string,
  deps: {
    label: string;
    existingNames: readonly string[];
    newId: () => string;
    now: () => string;
    importPretrained: (corpus: string) => Promise<J | null>;
    createJob: (draft: SampleJobDraft) => Promise<J>;
  },
): Promise<{ job: J; corpus: string; pretrained: boolean }> {
  if (!SAFE_SET.test(setId)) throw new Error('That sample set is not available.');
  const imported = await deps.importPretrained(setId);
  if (imported) return { job: imported, corpus: setId, pretrained: true };
  const draft = buildSampleJob({
    id: deps.newId(),
    label: deps.label,
    corpus: setId,
    existingNames: deps.existingNames,
    now: deps.now(),
  });
  return { job: await deps.createJob(draft), corpus: setId, pretrained: false };
}

const words = (text: string) =>
  ` ${text
    .toLocaleLowerCase()
    .replace(/[^a-z0-9&]+/g, ' ')
    .trim()} `;

/**
 * Which installed sample set fits this job, or null to leave the simulator's own
 * default. A sample job's name is its set's name; otherwise the job's name and its
 * document types are matched against each set's everyday words.
 */
export function sampleSetForJob(
  job: { name: string; documentTypes?: readonly { name: string }[] },
  stacks: readonly SampleStack[],
): string | null {
  const installed = stacks.filter((stack) => stack.documentCount > 0);
  if (job.name.startsWith(SAMPLE_JOB_PREFIX)) {
    const label = job.name
      .slice(SAMPLE_JOB_PREFIX.length)
      .replace(/ \d+$/, '')
      .trim()
      .toLocaleLowerCase();
    const exact = installed.find((stack) => stack.label.toLocaleLowerCase() === label);
    if (exact) return exact.corpus;
  }
  const text = words([job.name, ...(job.documentTypes ?? []).map((type) => type.name)].join(' '));
  let best: { corpus: string; score: number } | null = null;
  for (const stack of installed) {
    const keywords = [
      stack.label.toLocaleLowerCase(),
      ...(SET_KEYWORDS[setKey(stack.corpus)] ?? []),
    ];
    const score = keywords.reduce(
      (sum, keyword) => sum + (text.includes(words(keyword)) ? keyword.length : 0),
      0,
    );
    if (
      score > 0 &&
      (!best || score > best.score || (score === best.score && stack.corpus.endsWith('_small_set')))
    )
      best = { corpus: stack.corpus, score };
  }
  return best?.corpus ?? null;
}

// ---------------------------------------------------------------------------
// The setup wait: one card per stage, saying what comes next
// ---------------------------------------------------------------------------

export type SetupNextCard = { next: string; tip?: string };

const SETUP_CARDS: Record<string, SetupNextCard> = {
  preparing: {
    next: 'Reading the words on every page',
    tip: 'Your original files are never changed.',
  },
  recognizing: {
    next: 'Finding where each document starts and ends',
    tip: 'Barcodes and break sheets count too.',
  },
  organizing: {
    next: 'Naming the kinds of documents and their fields',
    tip: 'Each kind appears on the right as it is named.',
  },
  understanding: { next: "You check what it's unsure about" },
  finalizing: {
    next: 'Your documents open for checking',
    tip: 'Ctrl+Enter confirms and moves on.',
  },
};

export function setupNextCard(stage: string | undefined): SetupNextCard | null {
  return stage ? (SETUP_CARDS[stage] ?? null) : null;
}

// ---------------------------------------------------------------------------
// The first review: why a document needs review, in plain words
// ---------------------------------------------------------------------------

export type WaitingDocument = ProblemDocument & {
  needsReview: boolean;
  documentType?: string | null;
  confirmedAsWhole?: boolean;
  validationFindings?: readonly { severity?: string; code?: string | null }[];
  /** The engine's ReviewState.WaitReason codes, in checklist order. */
  waitReasons?: readonly string[] | null;
  /** Set when the field reader itself couldn't read (not-read.ts): no read-again promise then. */
  notReadCause?: string | null;
  /** For a field-confidence hold, the value it is about (uncertainFields, below). */
  fields?: readonly ConfidenceField[] | null;
};

/** The job as far as the waiting line reads it: its mode, and its types' field thresholds. */
export type WaitingJob = {
  reviewPolicy?: { processingMode?: string | null; fields?: number | null } | null;
  documentTypes?: readonly ({ name: string } & NonNullable<ConfidenceSchema>)[];
};

// The engine's reasons a document waits (ReviewState.WaitReason), in words.
// The engine decides and names the reason; these only say it.

/** Short labels for counts: "30 · Other problem". */
export const WAIT_REASON_LABELS: Record<string, string> = {
  'not-read': 'Not read yet',
  'no-type': 'No document type',
  'no-values': 'No values found', // PAIGE: the core's ReviewState.cs
  'manual-mode': 'Waiting for a person', // PAIGE: raised through the core's ReviewState.HostWaitReasons (ManualModeHold)
  'page-turned': 'Page turned', // PAIGE: the host's reason for a waiting document whose page a person turned (PageTurnHold.cs)
  'type-unconfirmed': 'Type not confirmed yet',
  'type-confidence': 'Unsure of the type',
  start: 'Unsure where it starts',
  'missing-required': 'Required value missing',
  'filing-value-missing': 'Value this job files by is missing',
  'field-confidence': 'Unsure of a value',
  'field-problem': 'Trouble with a value',
  arithmetic: 'Line doesn’t add up',
  'table-total': 'Lines don’t match the total',
  'line-page': 'Line doesn’t match the page',
  problem: 'Other problem',
  teaching: 'Example to teach the job',
  reopened: 'You put it back in review',
  'another-look': 'Needs another look after what you confirmed',
  'change-unconfirmed': 'Your change waits for your confirmation',
};

/**
 * Each reason as the end of "Needs review: …", the word the rest of the screen
 * uses for a held document ("Needs review", "Next to review"). One plain reason,
 * never the evidence or the threshold behind it (owner, 2026-10-03: the measured
 * "2 of 5 similar-looking documents…" line was jargon).
 */
export const WAIT_REASON_WORDS: Record<string, string> = {
  'not-read': 'it hasn’t been read yet',
  'no-type': 'it has no document type to file it by',
  'no-values': 'Cortexa found no values on this document', // PAIGE: the core's ReviewState.cs
  'manual-mode': 'this job has no setup yet and is in Manual, so a person checks each document', // PAIGE: raised through the core's ReviewState.HostWaitReasons (ManualModeHold)
  // PAIGE: a document that was waiting keeps waiting when its page is turned; the ledger holds it until a Confirm (PageTurnHold.cs).
  'page-turned': 'you turned a page, and Cortexa read it again',
  'type-unconfirmed': 'its document type isn’t confirmed for this job yet',
  'type-confidence': 'Cortexa isn’t sure of the document type',
  start: 'Cortexa isn’t sure where it starts',
  'missing-required': 'a required value is missing',
  // The engine marks the field itself with the sentence that names it ("Invoice Date is empty, and this job files by it.").
  'filing-value-missing': 'a value this job files by is missing',
  'field-confidence': 'Cortexa isn’t sure of a value',
  'field-problem': 'Cortexa had trouble with a value', // PAIGE: the reason line names the value (field-problem-words.ts)
  arithmetic: 'a line doesn’t add up',
  // Every line can add up and the table still miss a line (or read the totals as one): said as what it is.
  'table-total': 'the lines don’t add up to the page’s subtotal or total',
  'line-page': 'a line’s numbers don’t match the page',
  problem: 'something on it needs a look',
  teaching: 'it’s an example to teach this job. Others like it can go through on their own',
  reopened: 'you put it back in review',
  'another-look': 'you corrected a value like this on a similar document',
  // A person changed it while it waited; it goes when they confirm it, never on its own (B-09).
  'change-unconfirmed': 'you changed it, so it waits for you to confirm it',
};

/**
 * The one line that says why this document needs review, or null when it does not.
 * The reason is the engine's own (ReviewState.Settle stamps waitReasons with the
 * flag). Manual holds only what fails a check, so the mode is never the
 * reason; saying it was sent operators looking at thresholds that were not the cause.
 */
export function whyDocumentWaits(document: WaitingDocument, job: WaitingJob): string | null {
  if (!document.needsReview || document.confirmedAsWhole) return null;
  // A reason that names nothing ("something on it needs a look") points at no
  // field, so it says nothing; the specific reasons come first when there are any.
  const reasons = (document.waitReasons ?? []).filter((reason) => reason !== 'problem');
  const first = reasons[0];
  // Not read because the field reader can't read: reading it again can't fix that, so the line
  // promises nothing; the card under it says what is wrong (not-read.ts).
  const readerProblem =
    document.notReadCause === 'field-reader-missing' ||
    document.notReadCause === 'field-reader-broken';
  const words =
    first === 'not-read'
      ? readerProblem
        ? 'it hasn’t been read yet'
        : notReadWords() // PAIGE: read again on its own only where the host does that
      : first === 'field-problem'
        ? fieldProblemWords(document) // PAIGE: names the value (field-problem-words.ts)
        : // Names the value under its threshold, which the checklist marks too (uncertainFields, below).
          first === 'field-confidence'
          ? fieldConfidenceWords(document, schemaOf(document, job), job.reviewPolicy)
          : first
            ? WAIT_REASON_WORDS[first]
            : undefined;
  if (!words) return null;
  const more = reasons.filter((reason) => reason !== first).length;
  return `Needs review: ${words}${more ? ` (and ${more} more reason${more === 1 ? '' : 's'})` : ''}.`;
}

/** Not read: read again on its own where the host does that, else by the document's own button. */
const notReadWords = (): string =>
  hostHas('not-read-retry')
    ? 'it hasn’t been read yet. Paige reads it again on its own'
    : 'it hasn’t been read yet. Paige reads it again when Cortexa is back'; // PAIGE: its review unit reads it again (NotReadReads)

// ---------------------------------------------------------------------------
// Field-confidence holds: which value a document waits on
// ---------------------------------------------------------------------------

/**
 * The values behind a `field-confidence` hold, so the reviewer is pointed at them.
 *
 * The engine holds a document when a read value's confidence is under its field's threshold
 * (ReviewState.FieldReviewReason: not the type, not a table, not confirmed, not empty, and
 * `confidence < (field.reviewBelow ?? policy.fields)`). The checklist and the field rows only
 * marked a value the reader flagged (`needsReview`), so a value that was merely under its bar
 * held the document with nothing marked: "Needs review: Paige isn’t sure of a value" over
 * a panel of unmarked fields (Paige V2, Purchase Order WRPO-93748, 2026-10-05: Request Number,
 * restored by a learned rule at the confidence of the reading it restored, 0).
 *
 * Only a document the engine holds for this reason is answered, so the engine still decides
 * whether it waits; this only says which value. When no value is under the bar the screen knows
 * (a threshold the screen has not loaded), the least confident value is the one the engine's
 * check saw, so something is always marked. Ported from Paige V2's
 * packages/capturepoint/src/paige/field-confidence-holds.ts (e0bb6aff).
 */

/** ReviewPolicy.DefaultFields: the job-wide field threshold when none is saved. */
const DEFAULT_FIELD_BAR = 0.5;

export type ConfidenceField = Pick<FieldResult, 'name' | 'value'> &
  Partial<
    Pick<
      FieldResult,
      | 'confidence'
      | 'fieldId'
      | 'fieldKind'
      | 'verified'
      | 'evidencePageNumber'
      | 'evidenceLineIndexes'
    >
  >;

export type ConfidenceDocument = {
  needsReview?: boolean;
  confirmedAsWhole?: boolean;
  waitReasons?: readonly string[] | null;
  fields?: readonly ConfidenceField[] | null;
};

/** The schema's fields as far as their thresholds go. */
export type ConfidenceSchema =
  | {
      fields: readonly {
        id?: string;
        name: string;
        kind?: string | null;
        reviewBelow?: number | null;
      }[];
    }
  | null
  | undefined;

export type ConfidencePolicy = { fields?: number | null } | null | undefined;

/** The values the engine's field-confidence hold is about, least confident first; empty when it does not hold for that. */
export function uncertainFields(
  document: ConfidenceDocument,
  schema: ConfidenceSchema,
  policy: ConfidencePolicy,
): ConfidenceField[] {
  if (!document.needsReview || document.confirmedAsWhole) return [];
  if (!(document.waitReasons ?? []).includes('field-confidence')) return [];
  const judged = (document.fields ?? [])
    .filter((field) => isJudged(field, schema))
    .sort((left, right) => confidenceOf(left) - confidenceOf(right));
  const under = judged.filter((field) => confidenceOf(field) < barOf(field, schema, policy));
  return under.length > 0 ? under : judged.slice(0, 1);
}

/** The end of "Needs review: …" for a field-confidence hold, naming the value. */
export function fieldConfidenceWords(
  document: ConfidenceDocument,
  schema: ConfidenceSchema,
  policy: ConfidencePolicy,
): string {
  const [first, ...rest] = uncertainFields(document, schema, policy);
  if (!first) return 'Cortexa isn’t sure of a value';
  return rest.length === 0
    ? `Cortexa isn’t sure of ${first.name}`
    : `Cortexa isn’t sure of ${rest.length + 1} values, starting with ${first.name}`;
}

/** A value with no confidence on it clears every bar (the engine always sends one). */
const confidenceOf = (field: ConfidenceField): number => field.confidence ?? 1;

/** A value the field threshold judges: not the type, not a table, not confirmed, not empty. */
function isJudged(field: ConfidenceField, schema: ConfidenceSchema): boolean {
  if (field.name.toLowerCase() === 'document type' || field.verified) return false;
  if (!field.value?.trim()) return false;
  const kind = field.fieldKind ?? configuredOf(field, schema)?.kind;
  return kind?.toLowerCase() !== 'line-items';
}

/** The field's own threshold beside Required, else the job's (ReviewState.FieldBar). */
function barOf(field: ConfidenceField, schema: ConfidenceSchema, policy: ConfidencePolicy): number {
  const bar = configuredOf(field, schema)?.reviewBelow ?? policy?.fields ?? DEFAULT_FIELD_BAR;
  return Math.min(1, Math.max(0, bar));
}

function configuredOf(field: ConfidenceField, schema: ConfidenceSchema) {
  return schema?.fields.find(
    (item) =>
      (field.fieldId && item.id === field.fieldId) ||
      item.name.toLowerCase() === field.name.toLowerCase(),
  );
}

/** The document's type in the job, for its fields' thresholds. */
const schemaOf = (document: WaitingDocument, job: WaitingJob): ConfidenceSchema =>
  job.documentTypes?.find(
    (type) => type.name.toLowerCase() === (document.documentType ?? '').toLowerCase(),
  );

// ---------------------------------------------------------------------------
// The export card: one line above the documents list
// ---------------------------------------------------------------------------

/** What the engine last said about the batch being read, when one is. */
/**
 * `folder`: a folder is still being added, in waves. Its whole-folder count is
 * the status bar's to say; a wave's numbers are never the batch's.
 */
export type ReadingProgress = {
  stage?: string | null;
  completed?: number;
  total?: number;
  folder?: boolean;
} | null;

/**
 * What the export card above the documents list says, or null when it has nothing to do
 * (owner, October 4, 2026). It is for someone on All or Teach who may not know documents
 * are waiting to go out, on any day, not only before the first export. One line, no
 * destination or folder words: not set up says "N ready to export" with Set up export;
 * set up and sending only when someone says, "N ready to export" with Export; set up and
 * turned off, "Export is off" with the documents waiting, when there are any, and Turn on
 * (owner, 2026-10-31, reversing the October 4 rule that the card said nothing while export
 * was off: the empty list's Off scene that said it is gone). It says nothing while pages are
 * still being read (the status bar and the list say that), when nothing is ready, or when
 * the job sends on its own.
 *
 * Not set up or turned off, "ready" is the engine's review verdict on the batch (not held
 * for review, or confirmed by a person): the engine's export preview counts only an export
 * that is on. Set up and on, it is that preview (what Export would send). Export's own checks
 * still decide what leaves; the card reports what was sent.
 */
export type ExportCardState = { kind: 'set-up-export' | 'export' | 'off'; ready: number };
export function exportCardState({
  documents,
  setUp,
  sending,
  manual,
  readyToSend,
  reading = null,
}: {
  documents: readonly { needsReview: boolean; confirmedAsWhole?: boolean }[];
  /** The job has somewhere to send files. */
  setUp: boolean;
  /** Export is turned on. */
  sending: boolean;
  /** It sends only when someone says (not as soon as ready, not on a schedule). */
  manual: boolean;
  /** The engine's export preview count, once known. */
  readyToSend: number | null;
  /** Set while a batch is still being read: its documents are not judged yet. */
  reading?: ReadingProgress;
}): ExportCardState | null {
  if (reading) return null;
  const reviewed = () =>
    documents.filter((document) => !document.needsReview || document.confirmedAsWhole === true)
      .length;
  if (!setUp) {
    const ready = reviewed();
    return ready > 0 ? { kind: 'set-up-export', ready } : null;
  }
  // Off says so whatever is ready: nothing leaves this job until someone turns it on.
  if (!sending) return { kind: 'off', ready: reviewed() };
  if (!manual) return null;
  return readyToSend !== null && readyToSend > 0 ? { kind: 'export', ready: readyToSend } : null;
}

// ---------------------------------------------------------------------------
// On-device milestone times (kept on this computer only, in main's store)
// ---------------------------------------------------------------------------

/**
 * Main's per-install store records the times (so clearing browser storage
 * never resets them) and records exports itself, automatic ones included. The
 * renderer keeps the last copy main sent and asks main to note the firsts only
 * it sees.
 */
export type Milestone = OnboardingMilestone;
export type MilestoneTimes = Partial<Record<Milestone, string>>;

let recorded: MilestoneTimes = {};

export const MILESTONE_EVENT = 'capturepoint-onboarding-milestone';

/** Takes main's latest times; announces them only when something changed. */
export function acceptMilestones(times: MilestoneTimes | null | undefined): void {
  const next = { ...(times ?? {}) };
  if (JSON.stringify(next) === JSON.stringify(recorded)) return;
  recorded = next;
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(MILESTONE_EVENT));
}

export function currentMilestones(): MilestoneTimes {
  return recorded;
}

export function hasMilestone(milestone: Milestone): boolean {
  return typeof recorded[milestone] === 'string';
}

/** Asks main to record a first it cannot see itself; later ones are ignored there. */
export function noteMilestone(milestone: Milestone): void {
  if (hasMilestone(milestone)) return;
  const api = typeof window !== 'undefined' ? window.capturePointDesktop : undefined;
  void api
    ?.noteOnboardingMilestone?.(milestone)
    .then((view) => acceptMilestones(view.milestones))
    .catch(() => undefined);
}

/** Where the renderer kept the times before they moved to main. */
export const LEGACY_MILESTONE_KEY = 'capturepoint.onboarding.milestones.v1';

/** The old browser-storage times; null when there are none. */
export function readLegacyMilestones(storage: {
  getItem(key: string): string | null;
}): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(storage.getItem(LEGACY_MILESTONE_KEY) ?? 'null') as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function forgetLegacyMilestones(storage: { removeItem(key: string): void }): void {
  try {
    storage.removeItem(LEGACY_MILESTONE_KEY);
  } catch {
    /* blocked storage keeps nothing anyway */
  }
}

/**
 * Once per window: read main's times, hand over any old browser-storage ones,
 * and follow main's changes. Returns the unsubscribe.
 */
export function connectMilestones(): () => void {
  const api = typeof window !== 'undefined' ? window.capturePointDesktop : undefined;
  if (!api?.getEngagement) return () => undefined;
  let live = true;
  const accept = (view: { milestones?: MilestoneTimes } | null | undefined) => {
    if (live) acceptMilestones(view?.milestones);
  };
  const stop = api.onEngagementChanged?.(accept) ?? (() => undefined);
  void api
    .getEngagement()
    .then(accept)
    .catch(() => undefined);
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  const legacy = storage ? readLegacyMilestones(storage) : null;
  // Browser storage lets go of the old times only once main holds them.
  if (storage && legacy && api.adoptOnboardingMilestones)
    void api
      .adoptOnboardingMilestones(legacy)
      .then((view) => {
        forgetLegacyMilestones(storage);
        accept(view);
      })
      .catch(() => undefined);
  return () => {
    live = false;
    stop();
  };
}

// ---------------------------------------------------------------------------
// Handing a new sample job to its workspace: open the simulator on its set
// ---------------------------------------------------------------------------

export const SAMPLE_SCAN_EVENT = 'capturepoint-sample-scan';
let pendingSampleScan: { jobId: string; corpus: string; documents?: string[] } | null = null;

/** One-shot: the next workspace for `jobId` opens the simulated scanner on `corpus` (only `documents` of it, when given). */
export function requestSampleScan(jobId: string, corpus: string, documents?: string[]): void {
  pendingSampleScan = { jobId, corpus, documents };
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SAMPLE_SCAN_EVENT));
}

export function takeSampleScan(jobId: string): { corpus: string; documents?: string[] } | null {
  if (!pendingSampleScan || pendingSampleScan.jobId !== jobId) return null;
  const { corpus, documents } = pendingSampleScan;
  pendingSampleScan = null;
  return { corpus, documents };
}

// ---------------------------------------------------------------------------
// Setup type cards fill in as each type finishes
// ---------------------------------------------------------------------------

export type TypeCard = { name: string; fields: string[]; samples: number; rows: number };
type ReadyActivity =
  | {
      id?: string;
      tags?: readonly string[] | null;
      discoveredDocumentTypes?: readonly string[] | null;
    }
  | null
  | undefined;

const readyTypesByJob = new Map<string, Map<string, string[]>>();

/**
 * The engine reports a finished type's fields with the "bootstrap:fields" event
 * that counts it done. Remembered per job, so a card fills the moment its type is
 * ready rather than when the first document of that type has been read.
 */
export function rememberReadyType(jobId: string, activity: ReadyActivity): void {
  if (
    activity?.id !== 'bootstrap:fields' ||
    activity.discoveredDocumentTypes?.length !== 1 ||
    !activity.tags?.length
  )
    return;
  const name = activity.discoveredDocumentTypes[0].trim();
  if (!name) return;
  const ready = readyTypesByJob.get(jobId) ?? new Map<string, string[]>();
  ready.set(
    name,
    activity.tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0),
  );
  readyTypesByJob.set(jobId, ready);
}

export function forgetReadyTypes(jobId: string): void {
  readyTypesByJob.delete(jobId);
}

/** Cards with every ready type's fields filled in, and only the still-waiting names left pending. */
export function fillTypeCards(
  jobId: string,
  cards: readonly TypeCard[],
  pending: readonly string[],
): { cards: TypeCard[]; pending: string[] } {
  const ready = readyTypesByJob.get(jobId);
  if (!ready || ready.size === 0) return { cards: [...cards], pending: [...pending] };
  const filled = cards.map((card) =>
    card.fields.length === 0 && ready.has(card.name)
      ? { ...card, fields: ready.get(card.name)! }
      : card,
  );
  const shown = new Set(filled.map((card) => card.name));
  const stillPending: string[] = [];
  for (const name of pending) {
    const fields = ready.get(name);
    if (fields && !shown.has(name)) {
      filled.push({ name, fields, samples: -1, rows: -1 });
      shown.add(name);
    } else if (!fields) stillPending.push(name);
  }
  return { cards: filled, pending: stillPending };
}
